import { ComparisonResult } from './types';

/* ───── Constants ───── */
const DEAP_METADATA_KEYS = new Set([
  'id', 'breakerId', 'districtId', 'distributionRateId',
  'dateTooLong', 'currentProductId', 'appVersion',
]);

/** Path aliases: maps known GCP path segments to their DEAP equivalents */
const PATH_ALIASES: Record<string, string> = {
  'addresss': 'address',
};

/* ───── Internal helpers ───── */

/** Unwrap DEAP's {confidence, value} wrappers recursively */
export function normalizeDeapValue(obj: any): any {
  if (obj === null || obj === undefined) return null;
  if (typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) {
    const mapped = obj.map(normalizeDeapValue);
    return mapped.length === 0 ? null : mapped;
  }

  const keys = Object.keys(obj);
  if (keys.includes('confidence') && keys.includes('value') && keys.length <= 3) {
    const v = obj.value;
    if (Array.isArray(v) && v.length === 0) return null;
    return normalizeDeapValue(v);
  }

  const result: any = {};
  for (const [key, val] of Object.entries(obj)) {
    if (DEAP_METADATA_KEYS.has(key)) continue;
    result[key] = normalizeDeapValue(val);
  }
  return result;
}

/** Merge entries from source Map into target Map */
function mergeMaps(target: Map<string, string>, source: Map<string, string>) {
  source.forEach((v, k) => target.set(k, v));
}

/** Flatten an object to dot-notation paths -> string values */
export function flattenObject(obj: any, prefix = ''): Map<string, string> {
  const result = new Map<string, string>();
  if (obj === null || obj === undefined) return result;
  if (typeof obj !== 'object') {
    const strVal = String(obj);
    if (strVal !== '' && strVal !== 'null') {
      result.set(prefix, strVal);
    }
    return result;
  }
  if (Array.isArray(obj)) {
    if (obj.length > 0 && typeof obj[0] === 'object') {
      mergeMaps(result, flattenObject(obj[0], prefix));
    }
    return result;
  }

  for (const [key, val] of Object.entries(obj)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (val !== null && val !== undefined && typeof val === 'object' && !Array.isArray(val)) {
      mergeMaps(result, flattenObject(val, path));
    } else if (Array.isArray(val)) {
      mergeMaps(result, flattenObject(val, path));
    } else {
      if (val === null || val === undefined) continue;
      const strVal = String(val);
      if (strVal === '' || strVal === 'null') continue;
      result.set(path, strVal);
    }
  }
  return result;
}

/** Normalize a string for comparison: lowercase, trim, collapse whitespace */
function normalizeForCompare(s: string): string {
  return s.toLowerCase().trim().replace(/\s+/g, ' ');
}

/** Normalize a path by replacing known aliases (GCP->DEAP direction) */
function normalizePathAliases(path: string): string {
  return path.split('.').map(seg => PATH_ALIASES[seg] || seg).join('.');
}

/* ───── Main comparison ───── */

export function compareOcrResults(gcpProcessed: any, deapRaw: any): ComparisonResult | null {
  if (!gcpProcessed || !deapRaw) return null;

  const normalizedDeap = normalizeDeapValue(deapRaw);
  const gcpFlat = flattenObject(gcpProcessed);
  const deapFlat = flattenObject(normalizedDeap);

  const matchingPaths = new Set<string>();
  const differingPaths = new Set<string>();
  const missingInDeap = new Set<string>();
  const missingInGcp = new Set<string>();

  const matchedDeapPaths = new Set<string>();

  gcpFlat.forEach((gcpVal, gcpPath) => {
    const normalizedPath = normalizePathAliases(gcpPath);
    if (deapFlat.has(normalizedPath)) {
      matchedDeapPaths.add(normalizedPath);
      const deapVal = deapFlat.get(normalizedPath)!;
      if (normalizeForCompare(gcpVal) === normalizeForCompare(deapVal)) {
        matchingPaths.add(gcpPath);
      } else {
        differingPaths.add(gcpPath);
      }
    } else if (deapFlat.has(gcpPath)) {
      matchedDeapPaths.add(gcpPath);
      const deapVal = deapFlat.get(gcpPath)!;
      if (normalizeForCompare(gcpVal) === normalizeForCompare(deapVal)) {
        matchingPaths.add(gcpPath);
      } else {
        differingPaths.add(gcpPath);
      }
    } else {
      missingInDeap.add(gcpPath);
    }
  });

  deapFlat.forEach((_val, deapPath) => {
    if (!matchedDeapPaths.has(deapPath) && !gcpFlat.has(deapPath)) {
      const reverseNormalized = deapPath.split('.').map(seg => {
        for (const [alias, target] of Object.entries(PATH_ALIASES)) {
          if (seg === target) return alias;
        }
        return seg;
      }).join('.');
      if (!gcpFlat.has(reverseNormalized)) {
        missingInGcp.add(deapPath);
      }
    }
  });

  const deapTotal = matchingPaths.size + differingPaths.size + missingInGcp.size;
  const matchPercentage = deapTotal > 0
    ? (matchingPaths.size / deapTotal) * 100
    : 0;

  return { differingPaths, matchingPaths, missingInGcp, missingInDeap, matchPercentage };
}

/** Build sets of paths that differ for a specific side (GCP or DEAP) */
export function getDiffPathsForSide(
  comparison: ComparisonResult,
  side: 'gcp' | 'deap',
  sourceData: any,
): Set<string> {
  const paths = new Set<string>();
  const flatPaths = flattenObject(side === 'deap' ? normalizeDeapValue(sourceData) : sourceData);

  flatPaths.forEach((_val, flatPath) => {
    const matchesGcpPath = comparison.differingPaths.has(flatPath);
    const aliasedPath = side === 'deap'
      ? flatPath.split('.').map(seg => { for (const [alias, target] of Object.entries(PATH_ALIASES)) { if (seg === target) return alias; } return seg; }).join('.')
      : flatPath;
    const matchesAliased = !matchesGcpPath && comparison.differingPaths.has(aliasedPath);

    if (matchesGcpPath || matchesAliased) {
      const parts = flatPath.split('.');
      for (let i = 1; i <= parts.length; i++) {
        const p = parts.slice(0, i).join('.');
        paths.add(p);
        // For DEAP side, also highlight .value inside {confidence, value} wrappers
        if (side === 'deap') {
          paths.add(p + '.value');
        }
      }
    }
  });
  return paths;
}
